import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../components/ui/Dialog';
import { Button } from '../../../components/ui/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import { useClients } from '../../../core/hooks/useClients';
import type { Client } from '../../../core/models/Client';

interface ManageClientsDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  currentClientIds: string[];
  onUpdate: (clientIds: string[]) => Promise<void>;
}

export const ManageClientsDialog: React.FC<ManageClientsDialogProps> = ({
  open,
  onClose,
  organizationId,
  currentClientIds,
  onUpdate,
}) => {
  // For SUPER_ADMIN, passing null lets useClients fetch all clients (see hook implementation)
  const { clients, loading } = useClients(organizationId || null);
  const [selectedClientIds, setSelectedClientIds] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedClientIds(new Set(currentClientIds));
    }
  }, [open, currentClientIds]);

  const handleAddClient = (clientId: string) => {
    setSelectedClientIds((prev) => new Set([...prev, clientId]));
  };

  const handleRemoveClient = (clientId: string) => {
    setSelectedClientIds((prev) => {
      const next = new Set(prev);
      next.delete(clientId);
      return next;
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onUpdate(Array.from(selectedClientIds));
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedClients: Client[] = clients.filter((c) => selectedClientIds.has(c.id));
  const availableClients: Client[] = clients.filter((c) => !selectedClientIds.has(c.id));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Manage Client Projects</DialogTitle>
          <DialogDescription>
            Assign projects that this client can manage. Changes affect visibility for client users.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm font-medium">Add Project</p>
            <Select onValueChange={handleAddClient} disabled={loading || availableClients.length === 0}>
              <SelectTrigger>
                <SelectValue placeholder={loading ? 'Loading projects...' : 'Select a project to add'} />
              </SelectTrigger>
              <SelectContent>
                {availableClients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {availableClients.length === 0 && !loading && (
              <p className="text-xs text-muted-foreground">
                All projects are already assigned to this client.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">Client Projects</p>
            {selectedClients.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No projects are currently assigned to this client.
              </p>
            ) : (
              <div className="space-y-2">
                {selectedClients.map((client) => (
                  <div
                    key={client.id}
                    className="flex items-center justify-between rounded border px-3 py-2 text-sm"
                  >
                    <div>
                      <p className="font-medium">{client.name}</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveClient(client.id)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} loading={isSubmitting}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
